import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Archive, Lock, Unlock, TrendingUp, TrendingDown, Scale, BarChart3 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_LABELS = {
  personnel: 'Personnel', administrative: 'Administrative',
  project: 'Project', women_project: 'Women Project', uncategorised: 'Uncategorised',
};

const SnapshotKpis = ({ snap, testPrefix }) => (
  <div className="grid grid-cols-3 gap-3">
    <div className="rounded-lg p-3 bg-green-50 dark:bg-green-900/15">
      <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Income</p>
      <p className="text-base font-heading font-bold text-green-600" data-testid={`${testPrefix}-income`}>{formatCurrency(snap.total_income)}</p>
    </div>
    <div className="rounded-lg p-3 bg-red-50 dark:bg-red-900/15">
      <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingDown className="h-3 w-3" /> Expenses</p>
      <p className="text-base font-heading font-bold text-red-600" data-testid={`${testPrefix}-expenses`}>{formatCurrency(snap.total_expenses)}</p>
    </div>
    <div className="rounded-lg p-3 bg-blue-50 dark:bg-blue-900/15">
      <p className="text-xs text-muted-foreground flex items-center gap-1"><Scale className="h-3 w-3" /> Net Balance</p>
      <p className={`text-base font-heading font-bold ${snap.net_balance >= 0 ? 'text-blue-600' : 'text-orange-600'}`} data-testid={`${testPrefix}-net`}>{formatCurrency(snap.net_balance)}</p>
    </div>
  </div>
);

export default function YearArchivePage() {
  const { user, hasPermission } = useAuth();
  const [archives, setArchives] = useState([]);
  const [selected, setSelected] = useState(null); // archive doc
  const [compare, setCompare] = useState(null);
  const [records, setRecords] = useState({ income: [], expenses: [] });
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeYear, setCloseYear] = useState(String(new Date().getFullYear() - 1));
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [trends, setTrends] = useState([]);
  const [searchParams] = useSearchParams();
  const isSuperAdmin = user?.role === 'super_admin';

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/finance/archives`);
      setArchives(r.data.items || []);
      const t = await axios.get(`${API_URL}/api/finance/year-trends`);
      setTrends(t.data.years || []);
    } catch {
      toast.error('Failed to load archives');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const c = searchParams.get('close');
    if (c && isSuperAdmin) {
      setCloseYear(c);
      setCloseOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isSuperAdmin]);

  const openYear = async (a) => {
    setSelected(a);
    setCompare(null);
    setRecords({ income: [], expenses: [] });
    try {
      const [c, inc, exp] = await Promise.all([
        axios.get(`${API_URL}/api/finance/archives/${a.year}/compare`),
        axios.get(`${API_URL}/api/finance/archives/${a.year}/records`, { params: { record_type: 'income' } }),
        axios.get(`${API_URL}/api/finance/archives/${a.year}/records`, { params: { record_type: 'expenses' } }),
      ]);
      setCompare(c.data);
      setRecords({ income: inc.data.items, expenses: exp.data.items });
    } catch {
      toast.error('Failed to load year details');
    }
  };

  const doClose = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/finance/year-close`, { year: parseInt(closeYear) });
      toast.success(`Year ${r.data.year} closed and archived — the platform now starts afresh`);
      setCloseOpen(false);
      setConfirmText('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close year');
    } finally {
      setBusy(false);
    }
  };

  const doReopen = async (year) => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/finance/year-close/${year}/reopen`);
      toast.success(`Year ${year} reopened — records restored to active views`);
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reopen year');
    } finally {
      setBusy(false);
    }
  };

  const compareRow = (label, a, c) => (
    <TableRow key={label}>
      <TableCell className="font-medium">{label}</TableCell>
      <TableCell className="text-right">{formatCurrency(a)}</TableCell>
      <TableCell className="text-right">{formatCurrency(c)}</TableCell>
      <TableCell className={`text-right font-medium ${c - a >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
        {c - a >= 0 ? '+' : ''}{formatCurrency(c - a)}
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6" data-testid="year-archive-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <Archive className="h-6 w-6 text-primary" /> Year Archive
          </h2>
          <p className="text-muted-foreground">Close a financial year to store its data, start afresh, and compare past years with the current one</p>
        </div>
        {isSuperAdmin && (
          <Button onClick={() => setCloseOpen(true)} data-testid="close-year-btn">
            <Lock className="mr-2 h-4 w-4" /> Close a Year
          </Button>
        )}
      </div>

      {/* Multi-year trends */}
      {trends.length > 1 && (
        <Card data-testid="year-trends-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" /> Multi-Year Trends
            </CardTitle>
            <CardDescription>Income vs expenses across all archived years and the current year</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => `${(v / 1000000).toFixed(0)}M`} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="income" name="Income" fill="#22C55E" radius={[3, 3, 0, 0]} />
                <Bar dataKey="expenses" name="Expenses" fill="#EF4444" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {archives.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground" data-testid="no-archives">
            <Archive className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>No years have been closed yet.</p>
            {isSuperAdmin && <p className="text-sm mt-1">At the end of the year, click "Close a Year" to archive its data — the platform will start afresh while old data stays accessible here.</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {archives.map((a) => (
            <Card key={a.year} className={`cursor-pointer card-hover ${selected?.year === a.year ? 'ring-2 ring-primary' : ''}`}
              onClick={() => openYear(a)} data-testid={`archive-card-${a.year}`}>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-xl flex items-center justify-between">
                  {a.year} <Badge variant="outline"><Lock className="h-3 w-3 mr-1" /> Closed</Badge>
                </CardTitle>
                <CardDescription>
                  Closed by {a.closed_by_name} · {a.closed_at ? format(new Date(a.closed_at), 'PP') : ''}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                <p>Income: <strong className="text-green-600">{formatCurrency(a.snapshot.total_income)}</strong></p>
                <p>Expenses: <strong className="text-red-600">{formatCurrency(a.snapshot.total_expenses)}</strong></p>
                <p>Net: <strong className={a.snapshot.net_balance >= 0 ? 'text-blue-600' : 'text-orange-600'}>{formatCurrency(a.snapshot.net_balance)}</strong></p>
                <p className="text-xs text-muted-foreground">
                  {a.snapshot.record_counts.income + a.snapshot.record_counts.expenses} financial records archived
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selected && compare && (
        <Card data-testid="archive-detail">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading">Archived Year {selected.year}</CardTitle>
                <CardDescription>Snapshot, comparison with {compare.current_year}, and archived records</CardDescription>
              </div>
              {isSuperAdmin && (
                <Button variant="outline" size="sm" disabled={busy} onClick={() => doReopen(selected.year)} data-testid="reopen-year-btn">
                  <Unlock className="mr-2 h-3.5 w-3.5" /> Reopen Year
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="summary">
              <TabsList>
                <TabsTrigger value="summary" data-testid="tab-archive-summary">Summary</TabsTrigger>
                <TabsTrigger value="compare" data-testid="tab-archive-compare">Compare vs {compare.current_year}</TabsTrigger>
                <TabsTrigger value="income" data-testid="tab-archive-income">Income ({records.income.length})</TabsTrigger>
                <TabsTrigger value="expenses" data-testid="tab-archive-expenses">Expenses ({records.expenses.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="summary" className="mt-4 space-y-4">
                <SnapshotKpis snap={selected.snapshot} testPrefix="arch" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Expenses by Group</p>
                    {Object.entries(selected.snapshot.expenses_by_group || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between text-sm py-1 border-b last:border-0">
                        <span>{GROUP_LABELS[k] || k}</span><strong>{formatCurrency(v)}</strong>
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Maize Buy-Back</p>
                    <div className="text-sm space-y-1">
                      <p>Purchased: <strong>{(selected.snapshot.maize?.purchased_kg || 0).toLocaleString()} kg</strong></p>
                      <p>Spend: <strong className="text-red-600">{formatCurrency(selected.snapshot.maize?.spend || 0)}</strong></p>
                      <p>Revenue: <strong className="text-green-600">{formatCurrency(selected.snapshot.maize?.revenue || 0)}</strong></p>
                      <p>Margin: <strong>{formatCurrency(selected.snapshot.maize?.margin || 0)}</strong></p>
                      <p className="text-xs text-muted-foreground pt-1">{selected.snapshot.requisitions} requisitions · {selected.snapshot.withdrawals} withdrawals · Budget {formatCurrency(selected.snapshot.total_budget || 0)}</p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="compare" className="mt-4" data-testid="archive-compare-table">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metric</TableHead>
                      <TableHead className="text-right">{selected.year} (archived)</TableHead>
                      <TableHead className="text-right">{compare.current_year} (to date)</TableHead>
                      <TableHead className="text-right">Change</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {compareRow('Total Income', compare.archived.total_income, compare.current.total_income)}
                    {compareRow('Total Expenses', compare.archived.total_expenses, compare.current.total_expenses)}
                    {compareRow('Net Balance', compare.archived.net_balance, compare.current.net_balance)}
                    {compareRow('Maize Revenue', compare.archived.maize?.revenue || 0, compare.current.maize?.revenue || 0)}
                    {compareRow('Maize Margin', compare.archived.maize?.margin || 0, compare.current.maize?.margin || 0)}
                    {['personnel', 'administrative', 'project', 'women_project'].map((g) =>
                      compareRow(`${GROUP_LABELS[g]} Expenses`,
                        (compare.archived.expenses_by_group || {})[g] || 0,
                        (compare.current.expenses_by_group || {})[g] || 0))}
                  </TableBody>
                </Table>
              </TabsContent>

              {['income', 'expenses'].map((t) => (
                <TabsContent key={t} value={t} className="mt-4">
                  <div className="max-h-96 overflow-y-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>{t === 'income' ? 'Source' : 'Category'}</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {records[t].length === 0 ? (
                          <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">No archived {t} records</TableCell></TableRow>
                        ) : records[t].map((r) => (
                          <TableRow key={r.id}>
                            <TableCell>{r.date}</TableCell>
                            <TableCell className="capitalize">{((t === 'income' ? r.source : (r.subcategory || r.category)) || '').replace(/_/g, ' ')}</TableCell>
                            <TableCell className="max-w-[280px] truncate text-muted-foreground">{r.description || '—'}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(r.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Close year dialog */}
      <Dialog open={closeOpen} onOpenChange={(o) => { setCloseOpen(o); if (!o) setConfirmText(''); }}>
        <DialogContent className="max-w-md" data-testid="close-year-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Close Financial Year</DialogTitle>
            <DialogDescription>
              All income, expenses, requisitions, withdrawals and maize records of the year will be archived.
              Dashboards and reports start afresh — archived data stays accessible on this page and can be reopened anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Year to close *</Label>
              <Input type="number" value={closeYear} onChange={(e) => setCloseYear(e.target.value)} data-testid="close-year-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Type <strong>CLOSE {closeYear}</strong> to confirm</Label>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={`CLOSE ${closeYear}`} data-testid="close-year-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)}>Cancel</Button>
            <Button disabled={busy || confirmText !== `CLOSE ${closeYear}`} onClick={doClose} data-testid="close-year-confirm-btn">
              <Lock className="mr-2 h-4 w-4" /> Close & Archive Year
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
